import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortBy } from 'react-instantsearch';

import { type BettingInsightsUiSortingConfig, type EsIndex } from '~/configuration/types';
import { toSortSelectionValue } from '~/search-domain';
import { Dropdown as CxyDropdown } from '~/vendor/cxy/components/Dropdown';

export function SortDropdown<TIndex extends EsIndex>({
  indexName,
  isDisabled = false,
  sorting,
}: {
  indexName: TIndex;
  isDisabled?: boolean;
  sorting: BettingInsightsUiSortingConfig<TIndex>;
}): JSX.Element | null {
  const { canRefine, currentRefinement, options, refine } = useSortBy({
    items: sorting.options.map((item, index: number) => ({
      value: index === 0 ? indexName : `${indexName}${toSortSelectionValue(item)}`,
      label: item.label,
    })),
  });

  const { t } = useTranslation();

  const hasInitializedRef = useRef(false);

  // Memoize the default sort value to prevent unnecessary re-renders
  const defaultSortValue = useMemo(() => {
    return sorting.defaultOverride ? `${indexName}${toSortSelectionValue(sorting.defaultOverride)}` : indexName;
  }, [indexName, sorting.defaultOverride]);

  // Set the default sort once on mount if it differs from current
  useEffect(() => {
    if (hasInitializedRef.current || !currentRefinement) return;

    if (defaultSortValue !== currentRefinement) {
      refine(defaultSortValue);
    }
    hasInitializedRef.current = true;
  }, [defaultSortValue, currentRefinement, refine]);

  if (!options.length || !canRefine) return null;

  return (
    <CxyDropdown
      data-testid="sortby-dropdown"
      disabled={isDisabled}
      label={t('CX_PLUS_SORT_BY')}
      onValueChange={refine}
      options={options}
      value={currentRefinement}
    />
  );
}
